// iOS Safari Viewport Fixes

export const applyIOSViewportFixes = () => {
  // Check if we're on iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  
  if (!isIOS) return;

  // Fix 1: Handle viewport height changes when keyboard shows/hides
  const setViewportHeight = () => {
    // Use the smallest available height to avoid layout shifts
    const vh = Math.min(window.innerHeight, document.documentElement.clientHeight) * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  };

  // Fix 2: Prevent zoom on input focus
  const preventInputZoom = () => {
    const inputs = document.querySelectorAll('input, textarea, select');
    inputs.forEach(input => {
      if (input.style.fontSize === '') {
        input.style.fontSize = '16px';
      }
    });
  };

  // Fix 3: Handle safe area insets
  const handleSafeArea = () => {
    const root = document.documentElement;
    
    // Set CSS custom properties for safe area insets
    if (CSS.supports('padding: env(safe-area-inset-top)')) {
      root.style.setProperty('--safe-area-top', 'env(safe-area-inset-top)');
      root.style.setProperty('--safe-area-bottom', 'env(safe-area-inset-bottom)');
      root.style.setProperty('--safe-area-left', 'env(safe-area-inset-left)');
      root.style.setProperty('--safe-area-right', 'env(safe-area-inset-right)');
    } else {
      // Fallback for older iOS versions
      root.style.setProperty('--safe-area-top', '20px');
      root.style.setProperty('--safe-area-bottom', '0px');
      root.style.setProperty('--safe-area-left', '0px');
      root.style.setProperty('--safe-area-right', '0px');
    }
  };

  // Fix 4: Prevent bounce scrolling on body
  const preventBounceScrolling = () => {
    document.body.style.overscrollBehavior = 'none';
    document.documentElement.style.overscrollBehavior = 'none';
  };

  // Fix 5: Handle orientation changes
  const handleOrientationChange = () => {
    // Small delay to ensure viewport has updated
    setTimeout(() => {
      setViewportHeight();
      window.scrollTo(0, 0);
    }, 100);
  };

  // Fix 6: Address bar height compensation
  const compensateForAddressBar = () => {
    // Store initial height
    let initialHeight = window.innerHeight;
    
    const updateHeight = () => {
      const currentHeight = window.innerHeight;
      const difference = initialHeight - currentHeight;
      
      // If height decreased significantly (keyboard or address bar), adjust
      if (difference > 150) {
        document.documentElement.style.setProperty('--keyboard-height', `${difference}px`);
        document.body.classList.add('keyboard-visible');
      } else {
        document.documentElement.style.setProperty('--keyboard-height', '0px');
        document.body.classList.remove('keyboard-visible');
      }
    };

    return updateHeight;
  };

  // Apply all fixes
  setViewportHeight();
  preventInputZoom();
  handleSafeArea();
  preventBounceScrolling();

  const updateHeight = compensateForAddressBar();

  // Event listeners
  window.addEventListener('resize', () => {
    setViewportHeight();
    updateHeight();
  });

  window.addEventListener('orientationchange', handleOrientationChange);

  // Reapply input zoom prevention when new inputs are added
  const observer = new MutationObserver(preventInputZoom);
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  // Cleanup function
  return () => {
    window.removeEventListener('resize', setViewportHeight);
    window.removeEventListener('orientationchange', handleOrientationChange);
    observer.disconnect();
  };
};

// Utility function to check if running in standalone mode (PWA)
export const isStandalone = () => {
  return window.navigator.standalone === true || 
         window.matchMedia('(display-mode: standalone)').matches;
};

// Utility function to get safe area values
export const getSafeAreaInsets = () => {
  const style = getComputedStyle(document.documentElement);
  return {
    top: style.getPropertyValue('--safe-area-top') || '0px',
    bottom: style.getPropertyValue('--safe-area-bottom') || '0px',
    left: style.getPropertyValue('--safe-area-left') || '0px',
    right: style.getPropertyValue('--safe-area-right') || '0px'
  };
};